/**
 * The player card, drawn behind a name.
 *
 * E6's second zero-wire-cost category (`design/economy/economy-rebuild.md` §4.4: "None — meta
 * layer only"). A card is a frame colour, a fill and a corner badge — three values from the
 * shared catalogue, applied to whatever element carries the name. Nothing about it reaches the
 * match: the room never learns it, PLAYER_INFO never carries it, and the charm's byte is the
 * only cosmetic wiring in this sprint that touches the wire at all.
 *
 * Shared rather than written into the Career route, because the same plate has to hold the
 * name on every screen that shows a name — Career today, the scoreboard and Leaderboards when
 * each learns whose card to draw.
 *
 * `NO_CARD` (Standard Issue, what every account starts with) has no catalogue entry, and that
 * is the common case: it renders as an unadorned element, never as a fallback frame, so a
 * player who has bought nothing does not appear to be wearing something.
 */
import { NO_CARD, PLAYER_CARDS } from '../../../../shared/src/player-cards.js';

function hex(value: number): string {
  return `#${value.toString(16).padStart(6, '0')}`;
}

export interface PlayerCardPlateOptions {
  /** Extra classes for the plate itself — the caller owns layout, this owns the card. */
  className?: string;
}

/**
 * Wraps `content` in the plate for `cardId`.
 *
 * Always returns an element, so a caller never branches on "is a card worn": an unknown id and
 * `NO_CARD` both come back as a plain wrapper carrying the content and nothing else.
 */
export function playerCardPlate(
  cardId: number,
  content: Node,
  options: PlayerCardPlateOptions = {},
): HTMLElement {
  const plate = document.createElement('div');
  plate.dataset.playerCardPlate = String(cardId);
  plate.className = ['relative min-w-0', options.className ?? ''].filter(Boolean).join(' ');

  const definition = cardId === NO_CARD ? undefined : PLAYER_CARDS[cardId];
  if (!definition) {
    plate.append(content);
    return plate;
  }

  // The card IS the purchased colourway, so its frame and fill are the player's data and cannot be
  // painted kit art (a 9-slice would hide the fill). It is built as a glossy enamel plaque
  // instead: ink-edged, lit from the top, dropped off the page — never a flat rectangle.
  plate.classList.add(
    'rounded-lg', 'border-2', 'px-3', 'py-2', 'overflow-hidden',
    'shadow-[inset_0_2px_0_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(0,0,0,0.35),0_0_0_2px_var(--kit-ink),0_5px_8px_rgba(0,0,0,0.5)]',
  );
  plate.style.borderColor = hex(definition.frame);
  plate.style.background = hex(definition.background);

  const badge = document.createElement('img');
  badge.src = `/game/ui/cards/${definition.badge}.png`;
  badge.alt = '';
  badge.draggable = false;
  badge.className = 'absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 object-contain opacity-80 select-none';
  // A badge key whose art has not shipped drops out rather than leaving a broken-image glyph
  // or an empty reserved box — the same posture the shop's card tiles take.
  badge.addEventListener('error', () => badge.remove(), { once: true });

  // `pr-10` only when a badge is actually drawn, so a card whose art is missing does not keep
  // a hole in the plate where the name should run.
  plate.classList.add('pr-10');
  plate.append(content, badge);
  return plate;
}
